import React from 'react';
import { Grid, GridItem, Heading } from '@charlietango/ui';
import { TextVariant } from '../../styles/typography';
import RichText from '../RichText/RichText';
import { rem } from '../../styles/rem';
import InitiateFrontPage from '../InitiateFrontPage/InitiateFrontPage';
import FrontPageInitiateDeclarationViewModel from '../../modules/FrontPage/types/FrontPageInitiateDeclarationViewModel';
import { SchemaFrontendUserDataViewModel } from '../../api/api-schema';
import Image from '../Image/Image';
import { HeroViewModel } from './types/HeroViewModel';
import { colors } from '../../styles/colors';
import { readableHeadingTextWrap } from '../../styles/textWrap';

export type HeroProps = {
  initiateDeclaration?: FrontPageInitiateDeclarationViewModel;
  user?: SchemaFrontendUserDataViewModel;
  userReady?: boolean;
  heroProps?: HeroViewModel;
  resolveRichTextHref?: (href: string) => string;
};

function Hero(props: HeroProps) {
  const {
    initiateDeclaration,
    user,
    userReady,
    heroProps,
    resolveRichTextHref,
  } = props;
  const image = heroProps?.image;

  return (
    <Grid
      gridGap={[rem(40), rem(40), rem(32)]}
      sx={{
        '@media (min-width: 48em)': {
          alignItems: 'start',
        },
      }}
    >
      <GridItem
        size={[1, 1, image ? 1 / 2 : 1]}
        sx={{
          order: 2,
          '@media (min-width: 48em) and (max-width: 51.99em)': {
            gridColumn: 'span 4',
          },
          '@media (min-width: 48em)': {
            order: 1,
            alignSelf: 'center',
          },
        }}
      >
        <section
          sx={{
            width: null,
            maxWidth: null,
            px: [rem(20), 0, 0],
            '@media (min-width: 48em)': {
              maxWidth: '100%',
              width: '100%',
            },
          }}
        >
          <Heading
            as="h1"
            variant={TextVariant.Header3xl}
            sx={{
              mb: [rem(24), rem(24), rem(24)],
              ...readableHeadingTextWrap,
              '@media (min-width: 48em) and (max-width: 51.99em)': {
                fontSize: rem(40),
              },
              '@media (min-width: 52em) and (max-width: 62em)': {
                fontSize: rem(44),
              },
            }}
          >
            {heroProps?.intro?.title}
          </Heading>
          <RichText
            sx={{
              mb: [rem(40), rem(40), rem(40)],
              fontSize: [rem(18), rem(20)],
              lineHeight: 1.5,
              width: '100%',
              maxWidth: '100%',
              '@media (min-width: 48em)': {
                mb: rem(40),
              },
            }}
            variant={TextVariant.BodyLg}
            resolveHref={resolveRichTextHref}
          >
            {heroProps?.intro?.text}
          </RichText>
          {initiateDeclaration && (
            <InitiateFrontPage
              model={initiateDeclaration}
              user={user}
              userReady={userReady}
              {...initiateDeclaration}
            />
          )}
        </section>
      </GridItem>
      {image && (
        <GridItem
          size={[1, 1, 1 / 2]}
          sx={{
            order: 1,
            '@media (min-width: 48em) and (max-width: 51.99em)': {
              gridColumn: 'span 4',
            },
            '@media (min-width: 48em)': {
              order: 2,
              alignSelf: 'start',
            },
          }}
        >
          <div
            css={{
              marginLeft: rem(20),
              marginRight: rem(20),
              width: 'auto',
              aspectRatio: '1 / 1',
              overflow: 'hidden',
              backgroundColor: colors.components.partiHero.Background.Default,
              borderRadius: rem(18),

              '@media (min-width: 48em)': {
                marginLeft: 0,
                marginRight: 0,
                width: '100%',
                borderRadius: rem(18),
              },
            }}
          >
            <Image
              {...image}
              sizes={['100vw']}
              css={{
                display: 'block',
                objectFit: 'contain',
                objectPosition: 'center',
                width: '100%',
                height: '100%',
              }}
            />
          </div>
        </GridItem>
      )}
    </Grid>
  );
}

export default Hero;
